import { type Ref, computed, nextTick, ref, watch } from 'vue'
import type {
  InventoryFilter,
  InventoryMapActionType,
  InventoryMapBoundsCoords,
  InventoryMapCluster,
  InventoryMapGeometry,
  InventoryMapObjectData,
  InventoryMapObjectTypes,
  InventoryMapTypesDict,
} from './types.ts'
import { INVENTORY_MAP_NO_BIND_DEPENDENCY } from './constants.ts'
import type { MapComponentExpose } from './adapters/types.ts'
import { debounce } from 'lodash'
import {
  toServerFieldFilters,
  useLoadInventMapData,
  useLoadInventRawObjects,
  useLoadInventSquareCoords,
} from './api.ts'
import { featureCollection, nearestPoint, point } from '@turf/turf'
import { InventObject } from './classes/InventObject.ts'
import { InventObjectCreator } from './classes/InventObjectCreator.ts'
import { useNotification } from '@/shared/ui'
import { useTranslate } from '@/shared/lib'
import { useAuth } from '@/entities/Auth'
import type { Feature } from 'ymaps3'
import {
  DEFAULT_MAP_BOUNDS,
  getBoundsFitLocation,
  waitForMapContainerSize,
  canCreateMapObjectWithoutBindParent,
  resolveMapBindParentTypeIds,
  resolveClusterGridPx,
} from './helpers.ts'

/** Разворачивает геометрию объекта в плоский список вершин `[lon, lat]` — для поиска ближайшей точки привязки. */
const geometryVertices = (geometry: InventoryMapGeometry): [number, number][] => {
  if (geometry.type === 'Point') return [geometry.coordinates]
  if (geometry.type === 'LineString') return geometry.coordinates
  return geometry.coordinates.flat() as [number, number][]
}

export const useModel = (
  map: Ref<MapComponentExpose | undefined>,
  mapObjectId: Ref<number>,
  options?: { skipInitialBoundsFit?: Ref<boolean> },
) => {
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const center = ref<number[]>([30.3351, 59.9343])
  const zoom = ref<number>(18)
  const bounds = ref<number[][]>([])

  const isProject = ref(false)
  const action = ref<InventoryMapActionType>('select')
  const addObjectType = ref<InventoryMapObjectTypes | null>(null)

  /** Словарь `types` из ответа `/objects/map`, копится по мере загрузки вьюпорта. */
  const typesDict = ref<InventoryMapTypesDict>({})
  /** Сырые объекты вьюпорта (не свёрнутые в кластер) — мутируется при удалении (см. useRemoveInventObject). */
  const data = ref<InventoryMapObjectData[]>([])
  /** Те же объекты, обёрнутые в InventObject — единственный источник для рендера/выделения (сервер уже фильтрует). */
  const objectsList = ref<InventObject[]>([])
  /** Кластеры и «стопки» (`colocated`) вьюпорта — как отдал сервер, без клиентской агрегации. */
  const mapClusters = ref<InventoryMapCluster[]>([])
  /** Заготовка под легаси-справочник типов для WInventoryMapActions — не заполняется (см. ai/plan_cluster.md). */
  const inventObjectTypes = ref<InventoryMapObjectTypes[]>([])

  const selectedObjects = ref<number[]>([])
  /** Id раскрытых (`expand`) стопок — шлётся на каждом reload, чтобы `members` не пропадали при pan/zoom. */
  const expandedClusterIds = ref<string[]>([])

  /** Ид объектов которые используются для центрирования и выборки из урл */
  const preSelectedObjectIds = ref<string>('')

  /**
   * Функция загрузки объектов и кластеров вьюпорта карты
   */
  const { send, data: responseData, isPending } = useLoadInventMapData()
  const rawObjectsForSelection = useLoadInventRawObjects()
  const rawObjectsForBindParent = useLoadInventRawObjects()

  /** Only the latest viewport load may surface errors (avoids toast spam while zooming). */
  let viewportLoadSeq = 0
  let pendingFilterApply = false
  let isFilterWatchReady = false
  const isFilterReloading = ref(false)

  const isMapDataLoading = computed(() => isPending.value || isFilterReloading.value)

  const getViewportBounds = (): InventoryMapBoundsCoords | null => {
    const b = map.value?.entity.bounds as [[number, number], [number, number]] | undefined
    if (!b) return null
    const [[minLon, minLat], [maxLon, maxLat]] = b
    return { min_lon: minLon, min_lat: minLat, max_lon: maxLon, max_lat: maxLat }
  }

  const loadData = async (objectId: Ref<number>, currentFilter?: InventoryFilter) => {
    if (!map.value) return

    const bbox = getViewportBounds()
    if (!bbox) return

    const seq = ++viewportLoadSeq

    try {
      await send({
        bbox,
        zoom: Math.round(zoom.value),
        type_ids: currentFilter?.types ?? [],
        exclude_ids: selectedObjects.value,
        field_filters: toServerFieldFilters(currentFilter?.fieldFilters ?? []),
        expand: expandedClusterIds.value,
        grid_px: resolveClusterGridPx(zoom.value),
      })

      if (seq !== viewportLoadSeq) return

      if (pendingFilterApply) {
        pendingFilterApply = false
        isFilterReloading.value = false
        addSuccess(trs('itf.map_filter_applied'))
      }
    } catch (error: any) {
      if (seq !== viewportLoadSeq) return

      if (pendingFilterApply) {
        pendingFilterApply = false
        isFilterReloading.value = false
      }

      addError(
        trs(
          error?.response?.data?.data?.msg ||
            error?.response?.data?.error ||
            'request.unknown_error',
        ),
      )
      throw error
    }
  }

  watch(responseData, () => {
    if (!responseData.value || !map.value) return

    const payload = (responseData.value as { data?: any })?.data
    if (!payload) return

    typesDict.value = { ...typesDict.value, ...(payload.types ?? {}) }
    data.value = Array.isArray(payload.objects) ? payload.objects : []
    mapClusters.value = Array.isArray(payload.clusters) ? payload.clusters : []

    if (map.value?.entity?.center) {
      center.value = map.value.entity.center
    }
  })

  /**
   * Функция загрузки данных центра карты
   */
  const loadCenterMap = () => {
    const result = useLoadInventSquareCoords(mapObjectId, preSelectedObjectIds)

    /**
     * Дефолтная подгонка без `ids` должна выполниться один раз для объекта карты.
     * Подгонка по `ids` выполняется для каждого нового набора id из URL.
     */
    let defaultFitKey = ''
    let fittedPreSelectedKey = ''
    let lastPreSelectedObjectKey = ''
    let fitSeq = 0

    watch(
      [result.data, map, preSelectedObjectIds, result.isFetching],
      async () => {
        const seq = ++fitSeq
        await nextTick()
        if (seq !== fitSeq) return
        if (!map.value) return
        if (options?.skipInitialBoundsFit?.value) return

        /**
         * Источник границ: при наличии предвыбранных `ids` фитим по прямоугольнику
         * с сервера (ждём ответ запроса), иначе — по дефолтным границам региона.
         */
        const objectKey = String(mapObjectId.value)
        const ids = preSelectedObjectIds.value
        let coords = DEFAULT_MAP_BOUNDS

        if (ids) {
          const idsFitKey = `${objectKey}:${ids}`
          if (fittedPreSelectedKey === idsFitKey) return
          if (result.isFetching.value) return
          if (!result.data.value) return

          coords = result.data.value.data
        } else {
          const nextDefaultFitKey = `${objectKey}:default`
          if (defaultFitKey === nextDefaultFitKey) return
          if (lastPreSelectedObjectKey === objectKey) return
        }

        if (
          Number.isFinite(coords.min_lon) &&
          Number.isFinite(coords.min_lat) &&
          Number.isFinite(coords.max_lon) &&
          Number.isFinite(coords.max_lat)
        ) {
          /**
           * На перезагрузке карта появляется в DOM сразу после прелоадера, и без
           * ожидания layout-а размеры контейнера ещё нулевые — фит считался бы по
           * размеру окна и обрезал крайние объекты.
           */
          await waitForMapContainerSize()
          if (seq !== fitSeq) return
          if (!map.value) return

          const location = getBoundsFitLocation(coords, { fitWidth: !!ids })

          map.value.entity.setLocation(location)

          zoom.value = location.zoom
          center.value = location.center

          if (ids) {
            fittedPreSelectedKey = `${objectKey}:${ids}`
            lastPreSelectedObjectKey = objectKey
          } else {
            defaultFitKey = `${objectKey}:default`
          }
        }
      },
      { immediate: true },
    )

    /**
     * Прелоадер показываем только пока реально ждём ответ сервера (есть `ids`).
     * Без `ids` запрос отключён, поэтому vue-query навсегда остаётся в статусе
     * `pending` — в этом случае загрузки нет, фит делается по дефолтным границам.
     */
    const isPending = computed(() => !!preSelectedObjectIds.value && result.isPending.value)

    return { isPending }
  }

  const { hasElementPermission } = useAuth()

  /**
   * Функция превращения объектов сервера в InventObject по словарю типов
   */
  const formatingData = (objects: InventoryMapObjectData[]) => {
    const result: InventObject[] = []

    objects.forEach((item) => {
      const type = typesDict.value[String(item.type_id)]
      if (!type) {
        console.warn(`Skipping object ${item.id}: unknown type_id ${item.type_id}`)
        return
      }
      try {
        result.push(InventObjectCreator.create(item, type))
      } catch (e) {
        console.warn(`Skipping object ${item.id} (type_id: ${item.type_id}): ${e}`)
      }
    })

    return result.filter((item) => hasElementPermission(item.getObjectTypeId(), 'read'))
  }

  /* Следим за списком объектов и обновляем список элементов */
  watch(data, () => {
    objectsList.value = formatingData(data.value)
  })

  /* Следим за изменением action и сбрасываем addObjectType */
  watch(action, () => {
    addObjectType.value = null
  })

  /** Функции добавления объекта в список выбранных **/

  /** Функция проверки объекта на выделение */
  const isSelected = (object: InventObject) => {
    return selectedObjects.value.includes(object.getId())
  }

  /**
   * Функция добавления одного объекта в новый список
   * @param object
   */
  const selectObject = (object: InventObject) => {
    selectedObjects.value = [object.getId()]
  }

  /**
   * Функция добавления одного объекта в существующий список
   * @param object
   */
  const selectAddObject = (object: InventObject) => {
    const id = object.getId()

    if (isSelected(object)) {
      selectedObjects.value = selectedObjects.value.filter((item) => item !== id)
    } else {
      selectedObjects.value.push(id)
    }
  }

  /**
   * Функция очистки выделенных объектов.
   * Переприсваивает `selectedObjects` только если реально было что чистить —
   * иначе клик по пустому месту карты гонял бы лишний reload на каждый клик.
   */
  const selectedClear = () => {
    if (selectedObjects.value.length) {
      selectedObjects.value = []
    }
    editSelectObject.value = null
  }

  /**
   * Функция выделения объектов при выделении прямоугольной области.
   * Раньше фильтровала уже загруженный список — теперь объекты области могут быть
   * свёрнуты в кластер, поэтому запрашиваем «сырой» список отдельно (`/objects/geo`).
   * @param coords
   */
  const selectedHandler = async (coords: number[][]) => {
    const bbox: InventoryMapBoundsCoords = {
      min_lon: Math.min(coords[0][0], coords[1][0]),
      min_lat: Math.min(coords[0][1], coords[1][1]),
      max_lon: Math.max(coords[0][0], coords[1][0]),
      max_lat: Math.max(coords[0][1], coords[1][1]),
    }

    try {
      const rawObjects = await rawObjectsForSelection.send({
        bbox,
        type_ids: filter.value.types,
        field_filters: toServerFieldFilters(filter.value.fieldFilters),
      })

      formatingData(rawObjects).forEach((item) => selectAddObject(item))
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
    }
  }

  const filter = ref<InventoryFilter>({
    types: [],
    fields: [],
    fieldFilters: [],
  })
  const filterRequestKey = computed(() =>
    JSON.stringify({
      types: filter.value.types,
      fieldFilters: filter.value.fieldFilters,
    }),
  )

  /**
   * Следим за изменением фильтра типов отображаемых полей
   * и сохраняем выбор пользователя
   **/
  watch(
    () => filter.value.fields,
    debounce((value) => {
      const fields = JSON.parse(localStorage.getItem('map_filter_fields') || '{}')
      fields[mapObjectId.value] = value

      localStorage.setItem('map_filter_fields', JSON.stringify(fields))
    }, 300),
  )

  /** Следим за типом выбранного объекта и обновляем данные **/
  watch(
    mapObjectId,
    () => {
      /* Сбрасываем выбранные объекты и раскрытые стопки */
      selectedObjects.value = []
      expandedClusterIds.value = []
    },
    { immediate: true },
  )

  /**
   * `selectedObjects`/`expandedClusterIds` уходят в `exclude_ids`/`expand` тела запроса —
   * сами по себе они не «загрузка вьюпорта» (zoom/bounds не менялись), но должны сразу
   * дойти до сервера: только новый ответ реально разваливает объекты по клику на кластер
   * или, наоборот, собирает их обратно, когда выделение/раскрытие снято (клик мимо,
   * выбор другого объекта, сворачивание стопки). Без немедленного reload здесь объекты
   * остаются в прежнем виде до следующего pan/zoom.
   */
  watch(
    [selectedObjects, expandedClusterIds],
    () => {
      void loadDataFiltered(mapObjectId).catch(() => {})
    },
    { deep: true },
  )

  /**
   * Функция нахождения ближайшей связанной точки привязки среди объектов родительских типов.
   * Раньше искала по уже загруженному списку вьюпорта — теперь запрашивает «сырой»
   * список по текущей области (`/objects/geo`), т.к. нужный объект может быть в кластере.
   * @param coordinates
   */
  const getNearestRootObjectId = async (coordinates: any[]) => {
    if (
      !addObjectType.value ||
      !addObjectType.value?.properties.is_bind ||
      addObjectType.value?.dependency === INVENTORY_MAP_NO_BIND_DEPENDENCY
    )
      return Number(mapObjectId.value)

    const parentTypeIds = resolveMapBindParentTypeIds(addObjectType.value)
    const bbox = getViewportBounds()
    const rawObjects = bbox
      ? await rawObjectsForBindParent.send({ bbox, type_ids: parentTypeIds })
      : []

    if (!rawObjects.length) {
      if (canCreateMapObjectWithoutBindParent(addObjectType.value.functional_type)) {
        return Number(mapObjectId.value)
      }
      addError(trs('validate.not_root_object'))
      throw new Error(trs('validate.not_root_object'))
    }

    const points: any = featureCollection([])

    rawObjects.forEach((item) => {
      geometryVertices(item.geometry).forEach((coords) => {
        points.features.push(point(coords, { id: item.id }))
      })
    })

    const targetPoint = point(coordinates[0])
    const rootPoint = nearestPoint(targetPoint, points)

    return rootPoint.properties.id
  }

  const editSelectObject = ref<InventObject | null>(null)
  const editNewPoint = ref<any | Feature>(null)
  const isEditNewPoint = ref<boolean>(false)

  /** После перезагрузки данных синхронизируем editSelectObject с новым экземпляром из objectsList **/
  watch(objectsList, () => {
    if (!editSelectObject.value) return
    const id = editSelectObject.value.getId()
    const match = objectsList.value.find((o) => o.getId() === id)
    if (match && match !== editSelectObject.value) {
      editSelectObject.value = match
    }
  })

  /**
   * Функция задания значения отображения добавочной точки
   * @param value
   */
  const setIsEditNewPoint = (value: boolean) => {
    isEditNewPoint.value = value
    editNewPoint.value = null
  }

  const loadDataFiltered = (objectId: Ref<number>) => {
    return loadData(objectId, filter.value)
  }

  /** Debounced reload while panning/zooming — avoids parallel polygon requests and error toasts */
  const debouncedReload = debounce((objectId: Ref<number>) => {
    void loadDataFiltered(objectId).catch(() => {})
  }, 400)

  /** Reload data when map filters change */
  watch(filterRequestKey, () => {
    debouncedReload(mapObjectId)

    if (!isFilterWatchReady) {
      isFilterWatchReady = true
      return
    }

    pendingFilterApply = true
    isFilterReloading.value = true
  })

  return {
    center,
    zoom,
    bounds,
    isProject,
    action,
    addObjectType,
    inventObjectTypes,
    typesDict,
    objectsList,
    mapClusters,
    data,
    selectedObjects,
    expandedClusterIds,
    filter,
    editSelectObject,
    editNewPoint,
    isEditNewPoint,
    preSelectedObjectIds,

    loadData,
    loadDataFiltered,
    debouncedReload,
    selectObject,
    selectAddObject,
    isSelected,
    selectedClear,
    selectedHandler,
    getNearestRootObjectId,
    setIsEditNewPoint,
    loadCenterMap,
    isMapDataLoading,
  }
}
